import { Drawer, Box, Stack, Typography, Chip, Button, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HEADING_FONT } from "../theme/fonts";

// --- Spec palette (blue · light) ---
const BLUE = "#3B82F6";
const BLUE_DARK = "#1D4ED8";
const INK = "#0F172A";
const MUTED = "#64748B";
const FAINT = "#94A3B8";
const HAIRLINE = "#EEF1F6";
const TILE_BG = "#F8FAFC";

// Copyable label / value cell.
function Field({ label, value, mono, copyable, icon }) {
  const shown = value || "—";
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          color: FAINT,
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        {icon && <Box sx={{ color: MUTED, display: "flex", flexShrink: 0 }}>{icon}</Box>}
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 500,
            color: value ? INK : FAINT,
            fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          {shown}
        </Typography>
        {copyable && value && (
          <Tooltip title="Copy">
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(value)}
              sx={{ p: 0.25, color: MUTED, "&:hover": { color: BLUE } }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

function SectionLabel({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Box sx={{ color: BLUE, display: "flex" }}>{icon}</Box>
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontSize: 13,
          fontWeight: 700,
          color: INK,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

const tileSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
  p: 2,
  borderRadius: "12px",
  border: `1px solid ${HAIRLINE}`,
  background: TILE_BG,
  mb: 2.5,
};

// Right-side detail drawer for a single bank account (the "view" / eye action).
function BankAccountDetailDrawer({ account, open, onClose, onEdit, canEdit = true }) {
  const acc = account;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 520 },
            maxWidth: "100%",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {acc && (
        <>
          {/* Gradient header */}
          <Box
            sx={{
              position: "relative",
              background: `linear-gradient(150deg, ${BLUE_DARK} 0%, ${BLUE} 75%, #60A5FA 100%)`,
              color: "#fff",
              p: 3,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccountBalanceOutlinedIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.85 }}>
                    Bank Account
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 21,
                      fontWeight: 700,
                      lineHeight: 1.15,
                      wordBreak: "break-word",
                    }}
                  >
                    {acc.accountName || "(unnamed)"}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={onClose} sx={{ color: "#fff", mt: -0.5, ml: "auto", flexShrink: 0 }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {/* Badges: bank + country + GST */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {acc.bank && (
                <Chip
                  size="small"
                  label={acc.bank}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#fff",
                    background: "rgba(255,255,255,0.22)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              )}
              {acc.country && (
                <Chip
                  size="small"
                  label={acc.country}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#fff",
                    background: "rgba(255,255,255,0.16)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              )}
              {acc.gst && (
                <Chip
                  size="small"
                  label={`GST: ${acc.gst}`}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#fff",
                    background: "rgba(255,255,255,0.16)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {/* Account */}
            <SectionLabel icon={<CreditCardOutlinedIcon sx={{ fontSize: 18 }} />}>Account Details</SectionLabel>
            <Box sx={tileSx}>
              <Field label="Account Name" value={acc.accountName} copyable />
              <Field label="Bank" value={acc.bank} />
              <Field label="Account Number" value={acc.accountNumber} mono copyable />
              <Field label="IBAN Number" value={acc.ibanNumber} mono copyable />
              <Field label="IFSC Code" value={acc.ifscCode} mono copyable />
              <Field label="GST" value={acc.gst} />
            </Box>

            {/* Branch & Location */}
            <SectionLabel icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />}>Branch & Location</SectionLabel>
            <Box sx={tileSx}>
              <Field label="Country" value={acc.country} />
              <Field label="Branch" value={acc.branch} />
              <Field label="Location" value={acc.location} />
              <Field label="UPI (UPI ID / Phone No)" value={acc.gpayPaytmPhonepe} copyable />
            </Box>

            {/* Meta */}
            <SectionLabel icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}>Record</SectionLabel>
            <Box sx={tileSx}>
              <Field label="BAC Id" value={acc.id != null ? String(acc.id) : ""} mono />
              <Field label="Posted By" value={acc.postedBy} />
              <Field label="Posted On" value={acc.createdAt} />
              <Field label="Updated On" value={acc.updatedAt} />
            </Box>
          </Box>

          {/* Footer: Close / Edit */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ p: 2.5, borderTop: `1px solid ${HAIRLINE}`, flexShrink: 0 }}
          >
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                py: 1,
                borderRadius: "10px",
                color: MUTED,
                background: "#fff",
                border: `1px solid ${HAIRLINE}`,
                "&:hover": { background: TILE_BG, borderColor: "#DBE3EF" },
              }}
            >
              Close
            </Button>
            {canEdit && (
              <Button
                onClick={() => onEdit?.(acc)}
                fullWidth
                startIcon={<EditOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  borderRadius: "10px",
                  color: "#fff",
                  background: BLUE,
                  boxShadow: "none",
                  "&:hover": { background: BLUE_DARK, boxShadow: "none" },
                }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
}

export default BankAccountDetailDrawer;
